import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { GraphQLResponse, ProtoApiService } from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import {
    BankAccountType,
    FundingBatchDetail,
    FundingBatchDetailStatus,
    GetFundingBatchDetailRequest,
    InvoiceSummary,
    RedactedBankAccount,
    TransactionFeeType
} from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import { GetFundingBatchDetailResponse } from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import { FeatureFlagService } from '@lsq-frontend/common-ui';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import { startWith } from 'rxjs/operators';
import {
    Payable,
    PayablesInput,
    PayablesResponse,
    TransferTypeForSupplier
} from '../../model/models';

@Injectable({ providedIn: 'root' })
export class GetFundingBatchDetailService extends ProtoApiService<
    GetFundingBatchDetailRequest,
    GetFundingBatchDetailResponse
> {
    public requestType = GetFundingBatchDetailRequest;
    public responseType = GetFundingBatchDetailResponse;
    public endpointName = 'GetFundingBatchDetail';
    public serviceName = 'financing';
    private _isPayLaterEnabled = false;

    constructor(
        http: HttpClient,
        @Inject(SETTINGS) settings: ISettings,
        private _featureFlagService: FeatureFlagService
    ) {
        super(http, settings);

        this._featureFlagService
            .isOn(FeatureFlags.PayLater)
            .pipe(startWith(this._featureFlagService.isOnSync(FeatureFlags.PayLater)))
            .subscribe((isOn) => {
                this._isPayLaterEnabled = isOn;
                if (this._isPayLaterEnabled) {
                    this.endpointName = 'v1/query';
                    this.serviceName = 'financingGql';
                    this.usePascalForUrl = false;
                } else {
                    this.endpointName = 'GetFundingBatchDetail';
                    this.serviceName = 'financing';
                    this.usePascalForUrl = true;
                }
            });
    }
    public requestMapper(
        req: GetFundingBatchDetailRequest
    ): { query: string } | GetFundingBatchDetailRequest {
        if (this._isPayLaterEnabled) {
            const input: PayablesInput = {
                companyFilter: {
                    supplierIds: [req.refSellerKey]
                },
                hasTransferId: req.refFundingBatchKey
            };
            const query = jsonToGraphQLQuery({
                payables: {
                    __args: { input },
                    rows: {
                        isGroup: true,
                        name: true,
                        id: true,
                        supplierDiscountFee: {
                            cents: true,
                            currencyCode: true
                        },
                        amount: {
                            cents: true,
                            currencyCode: true
                        },
                        transferDetails: {
                            supplierPaymentAmount: {
                                cents: true,
                                currencyCode: true
                            }
                        },
                        relationship: {
                            buyer: { company: { name: true } }
                        }
                    },
                    total: {
                        invoiceCount: true,
                        transferDetailsSummary: {
                            paymentDate: true,
                            supplierPaymentAmount: {
                                cents: true,
                                currencyCode: true
                            },
                            transferTypeForSupplier: true,
                            totalDiscountFee: {
                                cents: true,
                                currencyCode: true
                            },
                            supplierDiscountFee: {
                                cents: true,
                                currencyCode: true
                            },
                            buyerPaymentAmount: {
                                cents: true,
                                currencyCode: true
                            },
                            payeeBankAccount: {
                                accountNumber: true,
                                bankName: true,
                                accountType: true
                            }
                        }
                    }
                }
            });
            return { query: `{${query}}` };
        }
        return req;
    }

    public responseMapper(
        res: GraphQLResponse<PayablesResponse> | GetFundingBatchDetailResponse
    ): GetFundingBatchDetailResponse {
        if (this._isPayLaterEnabled) {
            const { rows: payables, total } = (res as GraphQLResponse<PayablesResponse>).data
                .payables;
            return new GetFundingBatchDetailResponse({
                fundingBatchDetail: {
                    fundingDate: total.transferDetailsSummary.paymentDate,
                    totalFundingAmount: total.transferDetailsSummary.supplierPaymentAmount,
                    totalDiscountAmount: total.transferDetailsSummary.totalDiscountFee,
                    bankAccountDetails: {
                        accountName: total.transferDetailsSummary?.payeeBankAccount?.bankName,
                        redactedAccountNumber:
                            total.transferDetailsSummary?.payeeBankAccount?.accountNumber,
                        bankAccountType:
                            BankAccountType[
                                total.transferDetailsSummary?.payeeBankAccount?.accountType
                            ]
                    } as RedactedBankAccount,
                    status: mapSupplierTransferTypeToLegacyStatus(
                        total.transferDetailsSummary.transferTypeForSupplier
                    ),
                    totalApprovedAmount: total.transferDetailsSummary.buyerPaymentAmount,
                    totalEarlyPaidAmountFee: total.transferDetailsSummary.totalDiscountFee,
                    supplierDiscountAmount: total.transferDetailsSummary.supplierDiscountFee
                } as FundingBatchDetail,
                rows: payables.map((payable) => {
                    return {
                        refInvoiceKey: payable.id.toString(),
                        buyerName: payable.relationship.buyer.company.name,
                        invoiceNumber: payable.name,
                        finalApprovedAmount: payable.amount,
                        discountAmount: payable.supplierDiscountFee,
                        availableAmount: payable.transferDetails[0].supplierPaymentAmount,
                        supplierDiscountAmount: payable.supplierDiscountFee,
                        isGroup: payable.isGroup
                    } as InvoiceSummary;
                }),
                totalCount: total.invoiceCount
            });
        }
        return res as GetFundingBatchDetailResponse;
    }

    public get endpointUrl(): string {
        return this._isPayLaterEnabled ? super.endpointUrl.replace('/api', '') : super.endpointUrl;
    }
}

function mapSupplierTransferTypeToLegacyStatus(
    transferTypeForSupplier: TransferTypeForSupplier
): FundingBatchDetailStatus {
    switch (transferTypeForSupplier) {
        case TransferTypeForSupplier.REJECTED:
            return FundingBatchDetailStatus.REJECTED;
        case TransferTypeForSupplier.EARLY_PAYMENT:
            return FundingBatchDetailStatus.SENT;
        case TransferTypeForSupplier.SCHEDULED_EARLY_PAYMENT:
            return FundingBatchDetailStatus.SCHEDULED;
        case TransferTypeForSupplier.PAYMENT:
            return FundingBatchDetailStatus.PAID;
        case TransferTypeForSupplier.PENDING:
            return FundingBatchDetailStatus.IN_TRANSFER;
        default:
            return FundingBatchDetailStatus.UNKNOWN_FUNDING_STATUS;
    }
}
